import { Alert, Box, Button, Divider, Grid, Typography } from "@mui/material";
import { useEffect, useRef } from "react";
import { useAreaActions } from "./hooks/useAreaActions";
import { useAreasQuery } from "./hooks/useAreasQuery";
import { useScopeOfWork } from "../../context/ScopeOfWorkContext";
import { BaseModalComponent } from "../../../../../../common/BaseModalComponent";
import { SASButtonStyles } from "../../../../../../ui/styles/inputStyles";
import { AreaCard } from "./AreaCard";
import { QueryState } from "../../../../query-states/GlobalQueryState";
import { AreaModal } from "./AreaModal";
import { OtherAreas } from "./OtherAreas";

export const Areas = () => {
  const { state } = useScopeOfWork();

  const selectedCategory = state.category;
  const service = state.service;
  const squareFt = state.squareft;
  const selectedOtherAreas = state.otherAreas ?? [];

  const { areas, initialize } = useAreaActions();

  const areasQuery = useAreasQuery({
    categoryUid: selectedCategory?.name,
    squareFt,
  });

  const availableAreas = areasQuery.data?.items ?? [];

  const lastSquareFtInitializedRef = useRef(null);

  useEffect(() => {
    lastSquareFtInitializedRef.current = null;
  }, [selectedCategory?.uid, service?.uid]);

  useEffect(() => {
    if (!service) return;
    if (!squareFt) return;
    if (!areasQuery.isSuccess) return;
    if (!availableAreas.length) return;
    if (areas.length > 0) return;

    const numericSquareFt = Number(squareFt);

    if (lastSquareFtInitializedRef.current === numericSquareFt) return;

    initialize(availableAreas, numericSquareFt);
    lastSquareFtInitializedRef.current = numericSquareFt;
  }, [squareFt, service?.uid, areasQuery.isSuccess, availableAreas, areas.length, initialize]);
  if (!service) {
    return (
      <Box p={4}>
        <Alert severity="warning">Please select a service first.</Alert>
      </Box>
    );
  }

  if (areasQuery.isLoading || areasQuery.isError) {
    return <QueryState isLoading={areasQuery.isLoading} isError={areasQuery.isError} serviceName="Areas" />;
  }

  const areasToRender = (areas?.length ? areas : availableAreas) ?? [];
  const hasNoCatalogAreas = !areasToRender.length && selectedOtherAreas.length === 0;

  return (
    <Box
      sx={{
        mr: 4,
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {hasNoCatalogAreas && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No catalog areas for this selection. Use &quot;Add other areas&quot; below to add at least one area and continue.
        </Alert>
      )}

      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "flex-end",
          mb: 2,
        }}
      >
        <BaseModalComponent
          defaultSizes={{ width: "800", height: "800" }}
          modalLabel="Other Areas"
          modalKey="Other-Areas-Selector"
          elementTrigger={<Button sx={{ ...SASButtonStyles }}>Add other areas</Button>}
        >
          <OtherAreas />
        </BaseModalComponent>
      </Box>

      <Grid container spacing={2} sx={{ flexGrow: 1 }}>
        {areasToRender.map((area) => (
          <Grid item xs={12} sm={6} md={3} key={area.uid}>
            <BaseModalComponent
              defaultSizes={{ width: "600", height: "800" }}
              modalLabel={`Details of ${area.name}`}
              modalKey={`AreaModal-${area.uid}`}
              elementTrigger={
                <Box sx={{ cursor: "pointer" }}>
                  <AreaCard area={area} />
                </Box>
              }
            >
              <AreaModal areaUid={area.uid} service={service} />
            </BaseModalComponent>
          </Grid>
        ))}
      </Grid>

      {selectedOtherAreas.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 2 }} />

          <Typography sx={{ fontWeight: 800, mb: 1.5 }}>Other Areas ({selectedOtherAreas.length})</Typography>

          <Grid container spacing={2}>
            {selectedOtherAreas.map((area) => (
              <Grid item xs={12} sm={6} md={3} key={area.uid}>
                <BaseModalComponent
                  defaultSizes={{ width: "600", height: "800" }}
                  modalLabel={`Details of ${area.name}`}
                  modalKey={`OtherAreaModal-${area.uid}`}
                  elementTrigger={
                    <Box sx={{ cursor: "pointer" }}>
                      <AreaCard area={area} isOtherArea />
                    </Box>
                  }
                >
                  <AreaModal areaUid={area.uid} service={service} />
                </BaseModalComponent>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
};
